/**
 * Turnstile Configuration
 *
 * Type definitions, constants, and utilities for Cloudflare Turnstile integration.
 * Extracted from TurnstileWidget.tsx (Line Count Refactor 2025-11-21)
 */

export interface TurnstileWidgetProps {
  onSuccess: (token: string) => void;
  onError?: (error: string) => void;
  onExpire?: () => void;
  onTimeout?: () => void;
  theme?: 'light' | 'dark' | 'auto';
  size?: 'normal' | 'compact' | 'flexible';
  appearance?: 'always' | 'execute' | 'interaction-only';
  /** Execution mode: 'render' executes immediately, 'execute' waits for turnstile.execute() */
  execution?: 'render' | 'execute';
  refreshExpired?: 'auto' | 'manual' | 'never';
  retry?: 'auto' | 'never';
  language?: string;
  action?: string;
  cData?: string;
}

/**
 * Imperative handle exposed by TurnstileWidget via forwardRef.
 * Allows parent components to trigger a token reset without unmounting.
 */
export interface TurnstileHandle {
  /** Reset the widget to silently generate a new token */
  reset: () => void;
}

/**
 * Valid message types from Turnstile widget (single source of truth)
 */
const VALID_MESSAGE_TYPES = ['success', 'error', 'expire', 'timeout', 'ready', 'reset_ack'] as const;
type TurnstileMessageType = typeof VALID_MESSAGE_TYPES[number];

export interface TurnstileMessage {
  type: TurnstileMessageType;
  token?: string;
  error?: string;
}

/**
 * Turnstile script URL
 */
export const TURNSTILE_SCRIPT_URL = 'https://challenges.cloudflare.com/turnstile/v0/api.js';

/**
 * Allowed origins for WebView security
 * Prevents navigation away from Turnstile challenge
 */
export const ALLOWED_ORIGINS = [
  'about:blank',
  'https://challenges.cloudflare.com',
];

/**
 * Validate Turnstile site key exists
 *
 * @param siteKey - Cloudflare Turnstile site key from environment
 * @returns Error message if invalid, null if valid
 */
export function validateSiteKey(siteKey: string | undefined): string | null {
  if (!siteKey) {
    return 'EXPO_PUBLIC_TURNSTILE_SITE_KEY not configured';
  }
  return null;
}

/**
 * Parse message from Turnstile WebView
 *
 * @param data - Raw message data from WebView
 * @returns Parsed TurnstileMessage
 * @throws Error if message parsing fails
 */
export function parseTurnstileMessage(data: string): TurnstileMessage {
  const message = JSON.parse(data);

  if (!VALID_MESSAGE_TYPES.includes(message.type)) {
    throw new Error(`Unknown message type: ${message.type}`);
  }

  return message as TurnstileMessage;
}

/**
 * Check if Turnstile script is already loaded (web only)
 *
 * @returns true if script is loaded and ready
 */
export function isTurnstileScriptLoaded(): boolean {
  if (typeof window === 'undefined') {
    return false;
  }
  return typeof (window as any).turnstile !== 'undefined';
}

/**
 * Check if Turnstile script element exists in DOM (web only)
 *
 * @returns true if script tag is in the document
 */
export function isTurnstileScriptPending(): boolean {
  if (typeof document === 'undefined') {
    return false;
  }
  const existingScript = document.querySelector('script[src*="challenges.cloudflare.com/turnstile"]');
  return existingScript !== null;
}
